import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { AxiosError } from "axios";

import { endpoints, QueryKeys } from "constants/constants";
import { ApiTokenUpdateData, ServerError } from "types/Api";
import { useAuthApi } from "./useApi";
import { useAppSnackbar } from "./useAppSnackbar";
import { usePostAudit } from "./usePostAudit";
import { useSelectedOrganization } from "./useSelectedOrganization";

interface UseUpdateSelectedOrganizationApiTokenProps {
  tokenId: string;
}
export const useUpdateSelectedOrganizationApiToken = ({
  tokenId,
}: UseUpdateSelectedOrganizationApiTokenProps) => {
  const { t } = useTranslation();
  const { setSnackbarData } = useAppSnackbar();
  const queryClient = useQueryClient();
  const { auditErrorEvent, auditFormEvent } = usePostAudit();
  const { selectedOrganizationId } = useSelectedOrganization();
  const updateToken = useAuthApi<
    ApiTokenUpdateData & { orgId: string },
    unknown
  >(
    "put",
    endpoints.ORGANIZATION_API_TOKEN(selectedOrganizationId as string, tokenId)
  );

  const { isLoading, isSuccess, mutate } = useMutation(updateToken, {
    onSuccess: () => {
      auditFormEvent(
        "Update Organization Api Token",
        "Add Organization",
        "FORM_SENT"
      );
      setSnackbarData({ severity: "success", message: t("TokenUpdated") });
      return queryClient.invalidateQueries([
        QueryKeys.GET_ORGANIZATION_API_TOKENS,
        selectedOrganizationId,
      ]);
    },
    onError: (data: AxiosError<ServerError>) => {
      const message =
        data.response?.data.message ??
        "Update Organization Api Token unknown error";
      setSnackbarData({ severity: "error", message });
      auditErrorEvent(message, "Update Organization Api Token", "ERROR");
    },
  });

  const onUpdateApiToken = (data: ApiTokenUpdateData) =>
    mutate({ ...data, orgId: selectedOrganizationId as string });

  return {
    isLoading,
    isSuccess,
    onUpdateApiToken,
  };
};
